import React from "react";

import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  form: {
    width: "100%",
    maxWidth: 720,
    display: "grid",
    gap: 20
  },

  fieldGroup: {
    display: "grid",
    gap: 8
  },

  label: {
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 750
  },

  helper: {
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.45
  },

  input: {
    "& .MuiOutlinedInput-root": {
      minHeight: 52,
      borderRadius: 12,
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,.035)"
          : "#FFFFFF"
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,.12)"
          : "rgba(15,23,42,.14)"
    },

    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(21,94,239,.45)"
    },

    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#155EEF",
      borderWidth: 1
    },

    "& .MuiInputBase-input": {
      padding: "15px 16px",
      color: theme.palette.text.primary,
      fontSize: 14
    }
  },

  information: {
    maxWidth: 720,
    marginTop: 24,
    padding: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    borderRadius: 14,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(21,94,239,.25)"
        : "1px solid rgba(21,94,239,.16)",
    background:
      theme.palette.type === "dark"
        ? "rgba(21,94,239,.08)"
        : "rgba(21,94,239,.045)"
  },

  informationIcon: {
    width: 28,
    height: 28,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 9,
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 900,
    background: "#155EEF"
  },

  informationTitle: {
    color: theme.palette.text.primary,
    fontSize: 12.5,
    fontWeight: 800
  },

  informationText: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.5
  }
}));

export default function CompanyStep() {
  const classes = useStyles();

  const {
    company,
    updateCompany,
    nextStep,
    previousStep
  } = useSetup();

  const companyName = company?.name || "";
  const companySegment = company?.segment || "";

  const canContinue =
    companyName.trim().length > 1 &&
    companySegment.trim().length > 1;

  return (
    <KoraxOnboardingShell
      step={2}
      totalSteps={7}
    >
      <KoraxOnboardingContent
        eyebrow="Conhecendo seu negócio"
        title="Informações da empresa"
        description="Esses dados ajudam a Korax a entender o contexto da sua operação e personalizar as próximas etapas."
      >
        <div className={classes.form}>
          <div className={classes.fieldGroup}>
            <Typography className={classes.label}>
              Nome da empresa
            </Typography>

            <TextField
              className={classes.input}
              variant="outlined"
              fullWidth
              autoFocus
              placeholder="Ex.: Ateliê Rosa"
              value={companyName}
              onChange={event =>
                updateCompany({
                  name: event.target.value
                })
              }
              inputProps={{
                maxLength: 120
              }}
            />

            <Typography className={classes.helper}>
              Use o nome pelo qual seus clientes reconhecem sua empresa.
            </Typography>
          </div>

          <div className={classes.fieldGroup}>
            <Typography className={classes.label}>
              Segmento de atuação
            </Typography>

            <TextField
              className={classes.input}
              variant="outlined"
              fullWidth
              placeholder="Ex.: Costura e ajustes de roupas"
              value={companySegment}
              onChange={event =>
                updateCompany({
                  segment: event.target.value
                })
              }
              inputProps={{
                maxLength: 160
              }}
            />

            <Typography className={classes.helper}>
              Descreva de forma simples o principal serviço ou mercado da empresa.
            </Typography>
          </div>
        </div>

        <div className={classes.information}>
          <div className={classes.informationIcon}>
            IA
          </div>

          <div>
            <Typography className={classes.informationTitle}>
              Essas informações serão usadas no treinamento
            </Typography>

            <Typography className={classes.informationText}>
              Depois você poderá complementar os serviços, regras de atendimento e demais informações da operação.
            </Typography>
          </div>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        disableNext={!canContinue}
      />
    </KoraxOnboardingShell>
  );
}
